/**
 * Hilfsfunktionen fuer die Chartdaten des Main-Charts.
 *
 * Abhaengigkeitsfrei halten: wird in memoisierten Render-Pfaden aufgerufen.
 */

/**
 * News-Anzahl je Zeitraum (bucket -> Anzahl) auf die vorhandenen Chartzeilen
 * verteilen, ohne neue Zeilen anzulegen.
 *
 * Frueher bekam jeder News-Tag ohne Kurszeile eine eigene Zeile mit value=null
 * (Wochenenden, der heutige Tag nach dem letzten Schlusskurs, News ausserhalb des
 * Fensters). Beim Hovern lieferte Recharts dort nur die News-Bar im Payload, der
 * Tooltip zeigte die News-Anzahl als Kurs, und die X-Achse lief ueber den
 * letzten Kurs hinaus.
 *
 * Zuordnung:
 *  - gleicher Bucket (Tag/Woche/Monat) -> diese Zeile
 *  - Luecke zwischen zwei Zeilen (z. B. Wochenende) -> naechste Zeile (wie im Modell-Chart)
 *  - nach der letzten Zeile bis endTs (z. B. News von heute) -> letzte Zeile
 *  - vor startTs oder nach endTs -> verworfen
 *
 * @param {Array<{date: string}>} rows   nach Datum sortierte Chartzeilen
 * @param {Object<string, number>} countsByBucket
 * @param {Object} opts
 * @param {(date: string) => string|null} opts.bucketOf    Bucket-Schluessel einer Zeile
 * @param {(bucket: string) => string|null} opts.bucketDate Startdatum (YYYY-MM-DD) eines Buckets
 * @param {number} [opts.startTs] Fensteranfang (ms); Standard: erste Zeile
 * @param {number} [opts.endTs]   Fensterende (ms); Standard: letzte Zeile
 */
export const attachNewsCounts = (rows = [], countsByBucket = {}, opts = {}) => {
  const { bucketOf, bucketDate } = opts;
  if (!rows.length) return [];
  const toTs = (d) => (d ? new Date(d).getTime() : NaN);
  const rowTs = rows.map((row) => toTs(row?.date));
  const firstTs = rowTs.find(Number.isFinite);
  const lastTs = [...rowTs].reverse().find(Number.isFinite);
  const startTs = Number.isFinite(opts.startTs) ? Math.min(opts.startTs, firstTs) : firstTs;
  const endTs = Number.isFinite(opts.endTs) ? Math.max(opts.endTs, lastTs) : lastTs;

  const indexByBucket = new Map();
  rows.forEach((row, idx) => {
    const key = bucketOf(row?.date);
    if (key && !indexByBucket.has(key)) indexByBucket.set(key, idx);
  });

  const counts = new Array(rows.length).fill(0);
  Object.entries(countsByBucket || {}).forEach(([bucket, rawCount]) => {
    const count = Number(rawCount) || 0;
    if (count <= 0) return;
    let idx = indexByBucket.get(bucket);
    if (idx === undefined) {
      const ts = toTs(bucketDate(bucket));
      if (!Number.isFinite(ts) || ts < startTs || ts > endTs) return;
      idx = rowTs.findIndex((t) => Number.isFinite(t) && t >= ts);
      if (idx < 0) idx = rowTs.lastIndexOf(lastTs);
    }
    counts[idx] += count;
  });

  return rows.map((row, idx) => ({
    ...row,
    news_count: counts[idx],
    news_bar: counts[idx] > 0 ? counts[idx] : null,
  }));
};

/**
 * Backend-Timeframe (tf) der Kursreihe fuer die gewaehlte Frequenz.
 * Die Zeitraum-Buttons (1J, 5J, Max) bestimmen nur das Fenster; die Frequenz
 * bestimmt die Punktdichte. Vorher hing tf am Button (1J -> Monatswerte), sodass
 * "Daily" trotzdem Monats- oder Jahreswerte zeigte.
 */
export const seriesTfForFrequency = (frequency) => {
  if (frequency === 'weekly') return '1w';
  if (frequency === 'monthly') return '1m';
  return '1d';
};
